/**
 * Custom Error Classes for EZHOA Application
 *
 * These error classes provide structured error handling with:
 * - HTTP status codes
 * - Error metadata
 * - Type discrimination for better error handling in ErrorBoundary
 */

/**
 * Base application error class
 * All custom errors extend from this
 */
export class AppError extends Error {
  statusCode: number;
  code?: string;
  metadata?: Record<string, unknown>;

  constructor(
    message: string,
    statusCode = 500,
    code?: string,
    metadata?: Record<string, unknown>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.code = code;
    this.metadata = metadata;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      code: this.code,
      metadata: this.metadata,
    };
  }
}

/**
 * API-related errors (network, server responses, etc.)
 */
export class ApiError extends AppError {
  endpoint?: string;
  method?: string;

  constructor(
    message: string,
    statusCode = 500,
    endpoint?: string,
    method?: string,
    metadata?: Record<string, unknown>
  ) {
    super(message, statusCode, 'API_ERROR', metadata);
    this.endpoint = endpoint;
    this.method = method;
  }

  static fromResponse(response: Response, endpoint?: string, method?: string) {
    const statusCode = response.status;
    let message = `API request failed with status ${statusCode}`;

    // Customize message based on status code
    switch (statusCode) {
      case 400:
        message = 'Bad request - please check your input';
        break;
      case 401:
        message = 'Unauthorized - please sign in';
        break;
      case 403:
        message = "Forbidden - you don't have permission for this action";
        break;
      case 404:
        message = 'Resource not found';
        break;
      case 409:
        message = 'Conflict - resource already exists';
        break;
      case 422:
        message = 'Validation failed';
        break;
      case 429:
        message = 'Too many requests - please try again later';
        break;
      case 500:
        message = 'Internal server error - please try again';
        break;
      case 502:
        message = 'Bad gateway - service temporarily unavailable';
        break;
      case 503:
        message = 'Service unavailable - please try again later';
        break;
    }

    return new ApiError(message, statusCode, endpoint, method, {
      url: response.url,
      statusText: response.statusText,
    });
  }

  static async fromFetch(response: Response, endpoint?: string, method?: string) {
    try {
      const data = await response.json();
      return new ApiError(
        data.message || data.error || 'API request failed',
        response.status,
        endpoint,
        method,
        data
      );
    } catch {
      return ApiError.fromResponse(response, endpoint, method);
    }
  }
}

/**
 * Validation errors (form validation, data validation)
 */
export class ValidationError extends AppError {
  field?: string;
  fields?: Record<string, string[]>;

  constructor(
    message: string,
    field?: string,
    fields?: Record<string, string[]>,
    metadata?: Record<string, unknown>
  ) {
    super(message, 422, 'VALIDATION_ERROR', metadata);
    this.field = field;
    this.fields = fields;
  }

  static fromFields(fields: Record<string, string | string[]>) {
    const fieldMessages: Record<string, string[]> = {};

    for (const [field, errors] of Object.entries(fields)) {
      fieldMessages[field] = Array.isArray(errors) ? errors : [errors];
    }

    const firstField = Object.keys(fieldMessages)[0];
    const firstError = fieldMessages[firstField]?.[0];

    return new ValidationError(firstError || 'Validation failed', firstField, fieldMessages);
  }

  getFieldError(field: string): string | undefined {
    return this.fields?.[field]?.[0];
  }

  getAllErrors(): string[] {
    if (!this.fields) return [this.message];
    return Object.values(this.fields).flat();
  }
}

/**
 * Authentication errors (login, token, permissions)
 */
export class AuthError extends AppError {
  reason?: 'INVALID_CREDENTIALS' | 'TOKEN_EXPIRED' | 'NO_PERMISSION' | 'NOT_AUTHENTICATED';

  constructor(
    message: string,
    reason?: 'INVALID_CREDENTIALS' | 'TOKEN_EXPIRED' | 'NO_PERMISSION' | 'NOT_AUTHENTICATED',
    metadata?: Record<string, unknown>
  ) {
    super(message, 401, 'AUTH_ERROR', metadata);
    this.reason = reason;
  }

  static invalidCredentials(message = 'Invalid email or password') {
    return new AuthError(message, 'INVALID_CREDENTIALS');
  }

  static tokenExpired(message = 'Your session has expired - please sign in again') {
    return new AuthError(message, 'TOKEN_EXPIRED');
  }

  static noPermission(message = "You don't have permission to perform this action") {
    return new AuthError(message, 'NO_PERMISSION', { statusCode: 403 });
  }

  static notAuthenticated(message = 'Please sign in to continue') {
    return new AuthError(message, 'NOT_AUTHENTICATED');
  }
}

/**
 * Network errors (connection issues, timeouts)
 */
export class NetworkError extends AppError {
  originalError?: Error;

  constructor(
    message = 'Network connection failed - please check your internet connection',
    originalError?: Error,
    metadata?: Record<string, unknown>
  ) {
    super(message, 0, 'NETWORK_ERROR', metadata);
    this.originalError = originalError;
  }

  static timeout(message = 'Request timed out - please try again') {
    return new NetworkError(message);
  }

  static offline(message = 'You appear to be offline - please check your connection') {
    return new NetworkError(message);
  }
}

/**
 * Not found errors (404)
 */
export class NotFoundError extends AppError {
  resourceType?: string;
  resourceId?: string;

  constructor(
    message = 'Resource not found',
    resourceType?: string,
    resourceId?: string,
    metadata?: Record<string, unknown>
  ) {
    super(message, 404, 'NOT_FOUND', metadata);
    this.resourceType = resourceType;
    this.resourceId = resourceId;
  }
}

/**
 * Conflict errors (409)
 */
export class ConflictError extends AppError {
  resourceType?: string;

  constructor(
    message = 'Resource already exists',
    resourceType?: string,
    metadata?: Record<string, unknown>
  ) {
    super(message, 409, 'CONFLICT', metadata);
    this.resourceType = resourceType;
  }
}

/**
 * Type guards for error checking
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

export function isConflictError(error: unknown): error is ConflictError {
  return error instanceof ConflictError;
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Helper function to get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isAppError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}

/**
 * Helper function to log errors with context
 */
export function logError(error: unknown, context?: Record<string, unknown>): void {
  if (import.meta.env.MODE !== 'production') {
    console.error('Error occurred:', {
      error,
      context,
      timestamp: new Date().toISOString(),
    });
  }

  // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
  // trackError(error, context);
}
